import { Component, computed, effect, inject, input, OnInit, signal } from '@angular/core'
import {
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
  FormControl,
} from '@angular/forms'
import {
  BricksAutocompleteComponent,
  BricksDropdownComponent,
  BricksErrorComponent,
  BricksInputComponent,
  BricksOptionComponent,
} from '@enbw_bricks/ui'
import { DigitsOnlyDirective } from '../../../shared/directives/digits-only.directive'
import {
  Manufacturer,
  ManufacturerWithTurbines,
  WindResourceFormControls,
} from '../../../shared/models/technical-resource'
import { catchError, firstValueFrom, map, of } from 'rxjs'
import { ManufacturerService } from '../../../shared/services/manufacturer.service'
import { CustomValidators } from '../../../shared/validators/custom-validators'
import { FormatViewDirective } from '../../../shared/directives/format-view.directive'
import { ViewFormatterService } from '../../../shared/services/view-formatter.service'

export const OTHER_TURBINE_MANUFACTURER = 'anderer Hersteller'
export const OTHER_TURBINE_TYPE = 'anderer Turbinentyp'

@Component({
  selector: 'app-wind-specific-technical-data',
  imports: [
    BricksInputComponent,
    BricksDropdownComponent,
    BricksErrorComponent,
    ReactiveFormsModule,
    BricksAutocompleteComponent,
    BricksOptionComponent,
    DigitsOnlyDirective,
    FormatViewDirective,
  ],
  standalone: true,
  templateUrl: './wind-specific-technical-data.component.html',
  styleUrl: './wind-specific-technical-data.component.scss',
})
export class WindSpecificTechnicalDataComponent implements OnInit {
  readonly resource = input.required<FormGroup<WindResourceFormControls>>()

  private readonly manufacturerService = inject(ManufacturerService)

  private readonly turbineManufacturers = signal<string[]>([])
  protected readonly turbineManufacturersOptions = computed(() => [
    ...this.turbineManufacturers(),
    OTHER_TURBINE_MANUFACTURER,
  ])
  private readonly turbineTypes = signal<string[]>([])
  protected readonly turbineTypesOptions = computed(() => [...this.turbineTypes(), OTHER_TURBINE_TYPE])

  constructor() {
    effect(async () => {
      const manufacturers = await firstValueFrom(
        this.manufacturerService.getWindManufacturers().pipe(
          catchError(() => of([])),
          map((manufacturers: Manufacturer[]) => (manufacturers || []).map((manufacturer) => manufacturer.name))
        )
      )
      this.turbineManufacturers.set(manufacturers)
    })
  }

  ngOnInit() {
    this.resource().controls.turbineManufacturer.addValidators([this.manufacturerValidator()])
    this.resource().controls.turbineManufacturer.updateValueAndValidity()

    this.resource().controls.turbineManufacturer.valueChanges.subscribe((manufacturer) => {
      this.turbineTypes.set([])
      this.resource().controls.turbineType.reset()
      if (manufacturer && this.turbineManufacturers().includes(manufacturer)) {
        this.manufacturerService
          .getWindManufacturerTurbines(manufacturer)
          .pipe(
            map((manufacturersWithTurbines: ManufacturerWithTurbines) =>
              (manufacturersWithTurbines || []).turbineTypes.map((turbine) => turbine.name)
            )
          )
          .subscribe((turbines) => {
            this.turbineTypes.set(turbines)
          })
      }
    })
  }

  private manufacturerValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      if (!value) {
        return null // Let required validator handle empty values
      }

      if (!this.turbineManufacturersOptions().includes(value)) {
        return { invalidManufacturer: { value } }
      }

      return null
    }
  }

  getTurbineHubHeightError(control: FormControl): string {
    if (control.hasError('zeroIsNotAllowed')) {
      return CustomValidators.zeroIsNotAllowedErrorMessage
    } else if (control.hasError('min') || control.hasError('max')) {
      return 'Die Eingabe muss zwischen 10 und 250 liegen.'
    } else if (control.hasError('pattern')) {
      return CustomValidators.threeDecimalsErrorMessage
    }
    return ''
  }

  protected readonly ViewFormatterService = ViewFormatterService
}
